import { Divider } from "@mui/material";
import dayjs from "dayjs";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import { RoomList } from "@/features/trips/components/RoomList";
import type { BookingData, TripDetails } from "@/features/trips/types/types";

type Props = {
  trip: TripDetails;
};

export function TripDetailsCard({ trip }: Props) {
  const intl = useIntl();
  const tripDate = useWatch<BookingData, "tripDate">({ name: "tripDate" });
  const travellers = useWatch<BookingData, "travellers">({
    name: "travellers",
  });

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        defaultMessage: "Trip details",
        description: "Section heading for trip details summary",
      })}
      collapsible={false}
    >
      <DetailsList>
        <DetailsListItem
          title={intl.formatMessage({
            defaultMessage: "Travel start date",
            description: "Label for the trip start / departure date",
          })}
          value={dayjs(tripDate).format("D MMMM, YYYY")}
        />

        <DetailsListItem
          title={intl.formatMessage({
            defaultMessage: "Travel end date",
            description: "Label for the trip end / return date",
          })}
          value={dayjs(tripDate)
            .add(trip.durationInDays, "day")
            .format("D MMMM, YYYY")}
        />

        <DetailsListItem
          title={intl.formatMessage({
            defaultMessage: "Travel length",
            description: "Label for the trip duration / length",
          })}
          value={intl.formatMessage(
            {
              defaultMessage:
                "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
              description: "Trip duration label (e.g. '7 nights')",
            },
            {
              nights: trip.durationInDays - 1,
              days: trip.durationInDays,
            },
          )}
        />

        <DetailsListItem
          title={intl.formatMessage({
            defaultMessage: "Flight operator",
            description:
              "Label for the airline / flight operator shown on the trip details page",
          })}
          value={trip.airline}
        />
      </DetailsList>

      <Divider
        sx={{
          borderColor: "var(--color-stroke-neutral-soft)",
          my: "calc(var(--spacing)*3)",
        }}
      />

      <div className="mb-1 text-sm text-text-disabled-primary">
        {intl.formatMessage({
          defaultMessage: "Booked rooms",
          description:
            "Section heading for room configuration in the trip details summary",
        })}
      </div>
      <RoomList rooms={travellers} />
    </CollapsibleCard>
  );
}
